import { useEffect, useCallback, useMemo, useState, useContext } from "react";
import { Box, IconButton, CircularProgress } from "@mui/material";
import { DataGrid, GridCellModes, useGridApiRef } from "@mui/x-data-grid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useNavigate } from "react-router-dom";
import { TopBarContext } from "../../providers/TopBarProvider";
import { ToastContext } from "../../providers/ToastProvider";
import LineService from "../../services/LineService";
import CreationModal from "../molecules/CreationModal";
import CustomNoRows from "../atoms/CustomNoRows";

const Lines = () => {
  const apiRef = useGridApiRef();
  const [openModal, setOpenModal] = useState(false);
  const [focuseds, setFocuseds] = useState({});
  const [lines, setLines] = useState([]);
  const [error, setError] = useState(false);
  const [count, setCount] = useState(0);
  const [mySearch, setMySearch] = useState("");
  const [itemsLoading, setItemsLoading] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const { setTitle, setSearchOptions, setBtnPrimary } =
    useContext(TopBarContext);
  const { setShowToast, setToastMessage } = useContext(ToastContext);
  const lineService = useMemo(() => new LineService(), []);
  const navigate = useNavigate();
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 10,
    page: 0,
  });
  const columns = [
    {
      field: "description",
      headerName: "Línea",
      minWidth: 200,
      flex: 1,
      sorting: false,
      editable: true,
      renderCell: (item) => (
        <Box pl={1} sx={{ width: "100%" }}>
          {item.row.description}
        </Box>
      ),
    },
    {
      field: "edit",
      headerName: "Acciones",
      sortable: false,
      width: 135,
      renderCell: (row) => {
        const item = itemsLoading.find((i) => i.id === row.id);
        if (item?.id && item?.edit) {
          return (
            <Box mr={1}>
              Editando{" "}
              <CircularProgress
                color="primary"
                size={25}
                sx={{ verticalAlign: "text-bottom" }}
              />
            </Box>
          );
        }

        if (item?.id && !item?.edit) {
          return (
            <Box mr={1}>
              Eliminando{" "}
              <CircularProgress
                color="error"
                size={25}
                sx={{ verticalAlign: "text-bottom" }}
              />
            </Box>
          );
        }

        return (
          <>
            <IconButton
              onClick={() => focusToInput(row.id)}
              sx={{ float: "right" }}
            >
              {focuseds[row.id]?.description?.mode === GridCellModes.Edit ? (
                <Box
                  component={FontAwesomeIcon}
                  icon="check"
                  fontSize="1rem"
                  color="success.main"
                />
              ) : (
                <Box
                  component={FontAwesomeIcon}
                  icon="pencil"
                  fontSize="1rem"
                  color="secondary.main"
                />
              )}
            </IconButton>
            <IconButton
              onClick={() => deleteLine(row.id, row.row.description)}
              sx={{ float: "right", ml: 2 }}
            >
              <Box
                component={FontAwesomeIcon}
                icon="trash"
                fontSize="1rem"
                color="error.main"
              />
            </IconButton>
          </>
        );
      },
    },
  ];

  const getAll = useCallback(
    async (skipCount = 0, search) => {
      try {
        setIsLoading(true);

        const skip = skipCount * 10;
        if (lines.length !== 0 && lines.length > skip) {
          return;
        }

        const res = await lineService.getAll(10, skip, search);
        const totalCount = res.data.result.totalCount;
        const items = res.data.result.items;

        if (items.length === 0 && search === mySearch) {
          return;
        }

        setCount(totalCount);
        setLines([
          ...(search === mySearch ? lines : []),
          ...items.map((item) => ({
            ...item,
            id: Number(item.id),
            selectable: true,
          })),
        ]);
        setMySearch(search);
      } catch {
        setError(true);
      } finally {
        setIsLoading(false);
      }
    },
    [lineService, setLines, lines]
  );

  const focusToInput = (id) => {
    setFocuseds((oldFocuseds) => ({
      ...oldFocuseds,
      [id]: {
        description: {
          mode:
            oldFocuseds[id]?.description?.mode === GridCellModes.Edit
              ? GridCellModes.View
              : GridCellModes.Edit,
        },
      },
    }));
  };

  const editLine = async (id, oldValue) => {
    try {
      const cell = await apiRef.current.getCellElement(id, "description");
      const value = cell.firstChild.firstChild.value;

      if (value === oldValue) {
        return;
      }

      setItemsLoading((oldItems) => [
        ...oldItems,
        {
          id,
          edit: true,
        },
      ]);

      const regex = /^[a-zA-Z0-9ñÑ][a-zA-Z0-9 ñÑ]*$/;
      if (!regex.test(value)) {
        throw new Error("Pattern");
      }

      const payload = {
        id,
        description: value,
      };

      const res = await lineService.update(payload);

      if (!res.data.success) {
        return;
      }

      setLines((oldLines) => {
        const idx = oldLines.findIndex((line) => line.id === id);
        oldLines[idx].description = value;
        return [...oldLines];
      });
    } catch (error) {
      if (error.message === "Pattern") {
        await apiRef.current.setEditCellValue({
          id,
          field: "description",
          value: oldValue,
        });
      } else {
        setLines((oldLines) => [...oldLines]);
      }

      setToastMessage([
        {
          severity: "error",
          primary:
            error.message === "Pattern"
              ? "Entrada invalida"
              : `Error al intentar editar la línea ${oldValue}`,
        },
      ]);
      setShowToast(true);
    } finally {
      setItemsLoading((oldItems) => [
        ...oldItems.filter((item) => item.id !== id),
      ]);
    }
  };

  const deleteLine = async (id, oldValue) => {
    try {
      setItemsLoading((oldItems) => [
        ...oldItems,
        {
          id,
          edit: false,
        },
      ]);

      const res = await lineService.delete(id);

      if (!res.data.success) {
        return;
      }

      setLines((oldLines) => [...oldLines.filter((line) => line.id !== id)]);
      setCount(count - 1);
    } catch (error) {
      setToastMessage((oldToasts) => [
        ...oldToasts,
        {
          severity: "error",
          primary: error.response.data.error.message.includes("used")
            ? `La línea ${oldValue} esta en uso`
            : `Error al intentar eliminar la línea ${oldValue}`,
        },
      ]);
      setShowToast(true);
    } finally {
      setItemsLoading((oldItems) => [
        ...oldItems.filter((item) => item.id !== id),
      ]);
    }
  };

  useEffect(() => {
    setTitle("Líneas");
    setSearchOptions({
      label: "Buscar Línea",
      action: (search) => {
        getAll(0, search);
      },
    });
    setBtnPrimary({
      label: "Agregar Línea",
      action: () => setOpenModal(true),
      icon: "plus",
    });
  }, [setTitle, setSearchOptions, setBtnPrimary, navigate]);

  useEffect(() => {
    getAll();
  }, []);

  useEffect(() => {
    if (lines.length > 0) {
      getAll(paginationModel.page, mySearch);
    }
    setFocuseds({});
  }, [getAll, lines, paginationModel]);

  useEffect(() => {
    const nLines = lines.length;
    if (count < nLines) {
      setCount(nLines);
    }
  }, [lines]);

  const classes = {
    root: {
      height: "70vh",
      width: "100%",
      mt: 5,
    },
    datagrid: {
      marginTop: 5,
      fontSize: "1.1rem",
      ".MuiDataGrid-cell--editing input": {
        fontSize: "1.1rem",
      },
      ".MuiDataGrid-cell:focus,.MuiDataGrid-cell:focus-within": {
        outline: "none",
      },
    },
  };

  return (
    <Box sx={classes.root}>
      <DataGrid
        sx={classes.datagrid}
        apiRef={apiRef}
        columns={columns}
        rows={lines}
        rowCount={count}
        editMode="cell"
        getRowId={(row) => row.id}
        cellModesModel={focuseds}
        disableColumnMenu
        disableRowSelectionOnClick
        isRowSelectable={(item) => item.row.selectable}
        isCellEditable={(item) => item.row.selectable}
        pageSizeOptions={[10]}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        loading={isLoading}
        localeText={{
          toolbarColumns: "Columnas",
          toolbarColumnsLabel: "Seleccionar columnas",
          noRowsLabel: "Sin Colores",
          footerRowSelected: (count) =>
            count !== 1
              ? `${count.toLocaleString()} filas seleccionadas`
              : `${count.toLocaleString()} fila seleccionada`,
          MuiTablePagination: {
            labelDisplayedRows: ({ from, to, count }) =>
              `${from} - ${to} de ${count}`,
          },
        }}
        slotProps={{
          pagination: {
            labelRowsPerPage: "Filas por página",
          },
        }}
        slots={{
          noRowsOverlay: () => (
            <CustomNoRows
              message={
                error
                  ? "Error intentando obtener las líneas"
                  : "No hay líneas registradas"
              }
            />
          ),
          noResultsOverlay: () => (
            <CustomNoRows
              message={
                error
                  ? "Error intentando obtener las líneas"
                  : "No hay líneas registradas"
              }
            />
          ),
        }}
        onCellEditStop={(item, event) => {
          if (focuseds[item.id]?.description?.mode === GridCellModes.Edit) {
            focusToInput(item.id);
          }
          editLine(item.id, item.value);
        }}
      />
      <CreationModal
        open={openModal}
        handleClose={() => setOpenModal(false)}
        title="agregar línea"
        label="Nombre de la línea"
        keyword="línea"
        female={true}
        service={lineService}
        setRows={setLines}
      />
    </Box>
  );
};

export default Lines;
